import { ButtonType, BUTTON_SELECTOR } from '@ardoq/button';
import { ReactElement, useRef, useState, useLayoutEffect } from 'react';
import { s4 } from '@ardoq/design-tokens';
import styled, { css } from 'styled-components';
import { TabProps, TabListProps } from './types';
import { hideScrollbars } from '@ardoq/style-helpers';
import { tabPropsToDropdownOption } from './TabsPropsToDropdownOption';
import { ChevronTabButton } from './Tab';
import { calculateTabsOrder } from './calculateTabsOrder';
import { measureTabWidths } from './utils';
import { TAB_HOVER_HEIGHT, TAB_UNDERLINE_SPACE } from './consts';
import {
  ButtonWithDropdown,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { take, takeRight } from 'lodash';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { isTruthy } from '@ardoq/common-helpers';
import { useChevronSliders } from '@ardoq/hooks';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  width: 100%;

  /* Needed in order to keep the "More" button the same height
     as the tabs buttons */
  align-items: stretch;
  ${BUTTON_SELECTOR} {
    margin: 0 0 ${TAB_UNDERLINE_SPACE}px;
    /* Cover the case where all tabs are collapsed and only
       "More" dropdown button is visible
       setting explicit height to that button prevents the bar height drop */
    height: ${TAB_HOVER_HEIGHT}px;
    min-height: ${TAB_HOVER_HEIGHT}px;
  }
`;

const ChevronsWrapper = styled(Wrapper)`
  overflow: hidden;
`;

const TabRow = styled.div<{
  $fullButtons?: boolean;
  $isReady?: boolean;
}>`
  display: flex;
  overflow-x: auto;
  gap: ${s4};
  ${hideScrollbars};

  ${({ $fullButtons }) =>
    $fullButtons &&
    css`
      flex: 1;
    `};

  ${({ $isReady }) =>
    $isReady === false &&
    css`
      visibility: hidden;
    `};
`;

type ChevronOverflowProps = Pick<
  TabListProps,
  'fullButtons' | 'tablistAriaLabel'
> & {
  rootWrapperWidth: number;
  children: ReactElement<TabProps>[];
};

type DropdownOverflowProps = ChevronOverflowProps &
  Pick<
    TabListProps,
    | 'activeTabId'
    | 'onTabChange'
    | 'extraDropdownOptions'
    | 'clickNamespace'
    | 'moreButtonLabel'
  >;

export const ChevronOverflow = ({
  children,
  rootWrapperWidth,
  fullButtons,
  tablistAriaLabel,
}: ChevronOverflowProps) => {
  const {
    wrapperRef,
    wrapperOnScroll,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick,
    onRightChevronClick,
  } = useChevronSliders(rootWrapperWidth);

  return (
    <ChevronsWrapper>
      <ChevronTabButton
        isVisible={isLeftChevronVisible}
        direction="left"
        onClick={onLeftChevronClick}
      />
      <TabRow
        $fullButtons={fullButtons}
        role="tablist"
        ref={wrapperRef}
        onScroll={wrapperOnScroll}
        aria-label={tablistAriaLabel}
      >
        {children}
      </TabRow>
      <ChevronTabButton
        isVisible={isRightChevronVisible}
        direction="right"
        onClick={onRightChevronClick}
      />
    </ChevronsWrapper>
  );
};

// Fallback width used to reserve space for the "More" dropdown button until
// it has rendered and we can measure the actual width via ref. Sized for the
// English "More" default; once measured, the real `offsetWidth` (which can
// be larger for longer translated labels) is used.
const DROPDOWN_MORE_BUTTON_WIDTH_FALLBACK = 130;

type RenderProps = {
  childrenToShowInTheBar: ReactElement<TabProps>[];
  moreOptions: DropdownOption[];
  isReady: boolean;
};

const calculateTabDistribution = (
  tabWidths: Record<string, { tabId: string; width: number }>,
  activeTabId: string,
  availableWidth: number
) => {
  const { indexOfLastTabFittingToWrapper, sortedTabs } = calculateTabsOrder(
    `${activeTabId}`,
    Object.values(tabWidths),
    availableWidth
  );

  return {
    sortedTabIds: sortedTabs.map(tab => tab.tabId),
    numberOfTabsFitting: indexOfLastTabFittingToWrapper + 1,
  };
};

const splitChildrenByDistribution = (
  children: ReactElement<TabProps>[],
  sortedTabIds: string[],
  numberOfTabsFitting: number,
  onTabChange: (tabId: string) => void,
  activeTabId: string
) => {
  const childrenLookup = Object.fromEntries(
    children.map(child => [child.props.tabId, child])
  );

  const sortedChildren = sortedTabIds.map(tabId => childrenLookup[tabId]);

  const childrenForBar = take(sortedChildren, numberOfTabsFitting);
  const childrenInDropdown = takeRight(
    sortedChildren,
    sortedChildren.length - numberOfTabsFitting
  );

  const childrenForDropdown: DropdownOption[] = childrenInDropdown.map(
    ({ props }) =>
      tabPropsToDropdownOption(props, {
        onTabChange,
        activeTabId: `${activeTabId}`,
      })
  );

  return { childrenForBar, childrenForDropdown };
};

const combineDropdownOptions = (
  childrenOptions: DropdownOption[],
  extraOptions: DropdownOption[]
): DropdownOption[] => {
  const needsDivider = childrenOptions.length && extraOptions.length;
  const divider: DropdownOption | null = needsDivider
    ? { type: DropdownOptionType.DIVIDER, label: '' }
    : null;
  return [...childrenOptions, divider, ...extraOptions].filter(isTruthy);
};

const getRenderProps = (
  tabWidths: Record<string, { tabId: string; width: number }> | null,
  children: ReactElement<TabProps>[],
  rootWrapperWidth: number,
  activeTabId: string,
  onTabChange: (tabId: string) => void,
  extraDropdownOptions: DropdownOption[],
  moreButtonWidth: number
): RenderProps => {
  if (tabWidths === null) {
    return {
      childrenToShowInTheBar: children,
      moreOptions: [],
      isReady: false,
    };
  }

  const availableWidth = rootWrapperWidth - moreButtonWidth;
  const { sortedTabIds, numberOfTabsFitting } = calculateTabDistribution(
    tabWidths,
    `${activeTabId}`,
    availableWidth
  );

  const { childrenForBar, childrenForDropdown } = splitChildrenByDistribution(
    children,
    sortedTabIds,
    numberOfTabsFitting,
    onTabChange,
    activeTabId
  );

  const moreOptions = combineDropdownOptions(
    childrenForDropdown,
    extraDropdownOptions
  );

  return {
    childrenToShowInTheBar: childrenForBar,
    moreOptions,
    isReady: true,
  };
};

const haveTabsChanged = (oldTabs: string[], newTabs: string[]) =>
  oldTabs.sort().join(',') !== newTabs.sort().join(',');

export const DropdownOverflow = ({
  children,
  onTabChange,
  rootWrapperWidth,
  activeTabId,
  extraDropdownOptions = [],
  clickNamespace,
  fullButtons,
  moreButtonLabel = 'More',
  tablistAriaLabel,
}: DropdownOverflowProps) => {
  const childrenWrapperRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const [tabWidths, setTabWidths] = useState<Record<
    string,
    { tabId: string; width: number }
  > | null>(null);
  const [moreButtonWidth, setMoreButtonWidth] = useState(
    DROPDOWN_MORE_BUTTON_WIDTH_FALLBACK
  );

  const needsMeasure = haveTabsChanged(
    tabWidths ? Object.keys(tabWidths) : [''],
    children.map(child => child.props.tabId)
  );

  useLayoutEffect(() => {
    if (!childrenWrapperRef.current) return;
    if (!needsMeasure) return;

    const measured = measureTabWidths(childrenWrapperRef.current);
    setTabWidths(measured);
  }, [needsMeasure]);

  // Measure the rendered More button so the available-width reservation
  // tracks the actual translated label rather than the English-sized fallback.
  useLayoutEffect(() => {
    const measured = moreButtonRef.current?.offsetWidth;
    if (measured && measured !== moreButtonWidth) {
      setMoreButtonWidth(measured);
    }
  }, [moreButtonLabel, moreButtonWidth]);

  const { isReady, childrenToShowInTheBar, moreOptions } = getRenderProps(
    tabWidths,
    children,
    rootWrapperWidth,
    activeTabId,
    onTabChange,
    extraDropdownOptions,
    moreButtonWidth
  );

  return (
    <Wrapper>
      <TabRow
        $fullButtons={fullButtons}
        $isReady={isReady}
        role="tablist"
        ref={childrenWrapperRef}
        aria-label={tablistAriaLabel}
      >
        {isReady ? childrenToShowInTheBar : children}
      </TabRow>
      {isReady && Boolean(moreOptions.length) && (
        <div ref={moreButtonRef}>
          <ButtonWithDropdown
            options={moreOptions}
            alignMenuTo="right"
            dropdownSize={DropdownSize.M}
            clickNamespace={clickNamespace}
            buttonType={ButtonType.GHOST}
          >
            {moreButtonLabel}
          </ButtonWithDropdown>
        </div>
      )}
    </Wrapper>
  );
};
